import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators,
} from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppUtilityService } from 'src/app/app-utility.service';
import { PaySlipModel1 } from 'src/app/core/models/pay-slip.model';
import { NumbersOnlyDirective } from 'src/app/shared/directives';
import { MdbDatepickerModule, MdbModalModule, MdbModalRef } from 'src/lib/mdb5-adapted/public-api';

import { LmsModalFrameComponent } from '@lmsSharedUI';

@Component({
  selector: 'app-payslip-dialog',
  templateUrl: './payslip-dialog.component.html',
  styleUrls: ['./payslip-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MdbDatepickerModule,
    NumbersOnlyDirective,
    MdbModalModule,
    A11yModule,
    LmsModalFrameComponent
  ],
})
export class PayslipDialogComponent implements OnInit, OnDestroy {
  private readonly grossIncomeCaps: Record<string, number> = {
    weekly: 15000,
    fortnightly: 25000,
    monthly: 230000,
  };

  PayDatesFilter = (d: Date): boolean => {
    const day = d.getDay();
    return day !== 0 && day !== 6;
  };

  payslipModel: PaySlipModel1;
  payslipForm = this.fb.group({
    month: [0, [Validators.required]],
    payslipDate: [null as Date | null, [Validators.required]],
    grossIncome: [null as number | null, [Validators.required, this.grossIncomeCapValidator()]],
    nettIncome: [null as number | null, [Validators.required, this.netIncomeNotMoreThanGrossValidator()]],
    otherIncome: [0, [Validators.min(0)]]
  });
  submitted = false;
  payDateError: string;
  invalidDate = false;
  maxDate = new Date();
  clientType = '';
  month: number;
  // Custom properties for weekly/fortnightly tracking
  weekIndex?: number;
  fortnightIndex?: number;
  incomeType?: 'monthly' | 'weekly' | 'fortnightly';

  datePickerOptions = {
    format: 'yyyy-mm-dd',
    max: this.maxDate
  };
  dialogTitle = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MdbModalRef<PayslipDialogComponent>,
    public global_utility: AppUtilityService,
    public fb: FormBuilder
  ) {}

  ngOnInit(): void {
    // Initialize payslipModel if it's null or undefined
    if (!this.payslipModel) {
      this.payslipModel = {} as PaySlipModel1;
    }

    // Set the month property
    this.payslipModel.month = this.month;

    // Preserve custom properties if they exist
    if (this.weekIndex !== undefined) {
      (this.payslipModel as any).week = this.weekIndex;
    }
    if (this.fortnightIndex !== undefined) {
      (this.payslipModel as any).fortnight = this.fortnightIndex;
    }
    if (this.incomeType) {
      (this.payslipModel as any).incomeType = this.incomeType;
    }

    this.dialogTitle = this.getDialogTitle();
    // For SASSA and Pensioner clients, auto-populate gross income with nett income
    const isSassaOrPensioner = this.clientType === 'SASSA' || this.clientType === 'Pensioner';

    // Get existing values from payslipModel
    let existingGrossIncome = this.payslipModel.grossIncome;
    let existingNettIncome = this.payslipModel.nettIncome;
    let existingOtherIncome = this.payslipModel.otherIncome || 0;

    // For SASSA/Pensioner, ensure gross equals nett
    if (isSassaOrPensioner && existingNettIncome && !existingGrossIncome) {
      existingGrossIncome = existingNettIncome;
    }

    // Handle date value - ensure it's properly converted
    let payslipDateValue = this.payslipModel.payslipDate || this.payslipModel.date;
    let initialPayslipDate: Date | null = null;

    if (payslipDateValue) {
      initialPayslipDate = payslipDateValue instanceof Date
        ? payslipDateValue
        : new Date(payslipDateValue);

      // 1. Check if date is invalid
      if (isNaN(initialPayslipDate.getTime())) {
        initialPayslipDate = null;
      } else {
        // 2. Check if the date is today
        const isToday = initialPayslipDate.toDateString() === new Date().toDateString();
        if (isToday) {
          initialPayslipDate = null;
        }
      }
    }

    // Populate form with existing data
    this.payslipForm.patchValue({
      month: this.payslipModel.month || this.month,
      payslipDate: initialPayslipDate,
      grossIncome: existingGrossIncome,
      nettIncome: existingNettIncome,
      otherIncome: existingOtherIncome
    });

    // For SASSA and Pensioner clients, auto-update gross income when nett income changes
    if (isSassaOrPensioner) {
      this.payslipForm.get('nettIncome')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        if (value !== null && value !== undefined) {
          this.payslipForm.get('grossIncome')?.setValue(value, { emitEvent: false });
          this.grossIncome?.updateValueAndValidity({ emitEvent: false });
          this.nettIncome?.updateValueAndValidity({ emitEvent: false });
        }
      });
    } else {
      this.grossIncome?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.nettIncome?.updateValueAndValidity({ emitEvent: false });
      });
    }

    this.payslipForm.updateValueAndValidity({ emitEvent: false });

    console.log('Payslip Dialog initialized:', {
      month: this.month,
      clientType: this.clientType,
      week: this.weekIndex,
      fortnight: this.fortnightIndex,
      incomeType: this.incomeType,
      existingData: this.payslipModel
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Generates the dialog title based on the income type and month/week/fortnight
   */
  getDialogTitle(): string {
    if (this.incomeType === 'weekly' && this.weekIndex !== undefined) {
      return `Income ${this.month}-${this.weekIndex}`;
    } else if (this.incomeType === 'fortnightly' && this.fortnightIndex !== undefined) {
      return `Income ${this.month}-F${this.fortnightIndex}`;
    } else {
      return `Income ${this.month}`;
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  private getSelectedIncomeFrequency(): 'weekly' | 'fortnightly' | 'monthly' {
    if (this.incomeType === 'weekly' || this.clientType === 'Weekly') {
      return 'weekly';
    }

    if (this.incomeType === 'fortnightly' || this.clientType === 'Fortnightly') {
      return 'fortnightly';
    }

    return 'monthly';
  }

  private grossIncomeCapValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const grossIncome = Number(control.value ?? 0);
      const netIncome = Number(control.parent?.get('nettIncome')?.value ?? 0);

      if (!grossIncome) {
        return null;
      }

      const selectedFrequency = this.getSelectedIncomeFrequency();
      const incomeCap = this.grossIncomeCaps[selectedFrequency];

      return grossIncome > incomeCap ? { grossIncomeCap: true } : null;
    };
  }

  private netIncomeNotMoreThanGrossValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const netIncome = Number(control.value ?? 0);
      const grossIncome = Number(control.parent?.get('grossIncome')?.value ?? 0);

      if (!netIncome || !grossIncome) {
        return null;
      }

      return netIncome > grossIncome ? { netExceedsGross: true } : null;
    };
  }

  save() {
    this.submitted = true;

    if (this.payslipForm.invalid) {
      this.global_utility.warning('Please enter required payslip details', 'Income details');
      return;
    }

    // Handle date properly
    const dateInput = this.payslipForm.value.payslipDate;
    let dateValue: Date;

    if (dateInput instanceof Date) {
      dateValue = dateInput;
    } else if (typeof dateInput === 'string') {
      dateValue = new Date(dateInput);
    } else {
      dateValue = new Date();
    }

    // Ensure date is valid
    if (isNaN(dateValue.getTime())) {
      dateValue = new Date();
    }

    // Get form values
    const grossValue = this.payslipForm.value.grossIncome ?? 0;
    const nettValue = this.payslipForm.value.nettIncome ?? 0;
    const otherValue = this.payslipForm.value.otherIncome ?? 0;

    // Build the payslip model with all data
    const savedPayslip: PaySlipModel1 = {
      ...this.payslipModel,
      month: this.payslipForm.value.month ?? this.month,
      date: dateValue,
      payslipDate: dateValue,
      grossIncome: grossValue,
      nettIncome: nettValue,
      otherIncome: otherValue
    };

    // For weekly/fortnightly, ensure gross equals nett if not specified
    if ((this.incomeType === 'weekly' || this.incomeType === 'fortnightly') && grossValue === 0 && nettValue > 0) {
      savedPayslip.grossIncome = nettValue;
    }

    // Preserve custom properties
    if (this.weekIndex !== undefined) {
      (savedPayslip as any).week = this.weekIndex;
    }
    if (this.fortnightIndex !== undefined) {
      (savedPayslip as any).fortnight = this.fortnightIndex;
    }
    if (this.incomeType) {
      (savedPayslip as any).incomeType = this.incomeType;
    }

    console.log('Saving payslip:', savedPayslip);
    return this.dialogRef.close(savedPayslip);
  }

  get payslipInput() {
    return this.payslipForm?.controls;
  }

  get payslipDate() {
    return this.payslipForm?.get('payslipDate');
  }

  get grossIncome() {
    return this.payslipForm?.get('grossIncome');
  }

  get nettIncome() {
    return this.payslipForm?.get('nettIncome');
  }

  get otherIncome() {
    return this.payslipForm?.get('otherIncome');
  }
}
